import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import AnalyticsStatType from "../../data/analytics-stat-type";
import LoadingDots from "../../elements/loading-dots";
import VelavuSegmentedControls from "../../elements/velavu-segmented-controls";
import whitelabel from "../../whitelabel";
import * as styles from "./analytics-status-bar.module.scss";

enum DisplayType {
	Ready,
	Syncing,
	Blank,
	Incomplete,
}

export default function AnalyticsStatusBar(props: {
	type: AnalyticsStatType;
	onChangeType: (type: AnalyticsStatType) => void;
	selectionCount: number;
	syncing?: boolean;
	dataIncomplete?: boolean;
}) {
	const { t } = useTranslation();

	const statLabels = useMemo(
		() => [
			t("analytics.stat_temperature"),
			t("analytics.stat_humidity"),
			t("analytics.stat_battery"),
			t("analytics.stat_location"),
		],
		[t],
	);

	const displayType = useMemo(() => {
		if (props.syncing) {
			return DisplayType.Syncing;
		} else if (props.dataIncomplete) {
			return DisplayType.Incomplete;
		} else if (props.selectionCount === 0) {
			return DisplayType.Blank;
		} else {
			return DisplayType.Ready;
		}
	}, [props.syncing, props.dataIncomplete, props.selectionCount]);

	return (
		<div className={styles.content}>
			<VelavuSegmentedControls
				selectedIndex={props.type}
				onSelectIndex={props.onChangeType}
				labels={statLabels}
			/>
			<div className={styles.statusMeta}>
				{displayType === DisplayType.Syncing && (
					<>
						<span>{t("analytics.fetching")}</span>
						<LoadingDots
							className={styles.selectionLoadingDots}
							size={22}
							color={whitelabel.palette.secondary500}
						/>
					</>
				)}
				{displayType === DisplayType.Ready && (
					<span>
						{t("analytics.displaying_results", {
							count: props.selectionCount,
						}).toLowerCase()}
					</span>
				)}
				{displayType === DisplayType.Blank && (
					<span>{t("analytics.select_items").toLowerCase()}</span>
				)}
				{displayType === DisplayType.Incomplete && (
					<span className={styles.dataIncompleteWarning} role="alert">
						{t("analytics.data_incomplete")}
					</span>
				)}
			</div>
		</div>
	);
}
